import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { KanbanProject, KanbanBoard, KanbanLane, KanbanTask, KanbanLaneConfig, KanbanLaneConfigInput, KanbanFilterState, HistoriaUsuarioRevision } from '../models';
import { ParsingUtilities } from '../../../helpers/parsing-utilities';
import { SKIP_ERROR_HANDLER } from '../../../core/interceptors/error.interceptor';

@Injectable({ providedIn: 'root' })
export class KanbanApiService {

  private readonly baseUrl = environment.backEndHost + environment.serverEndPoint + '/kanban';

  constructor(private readonly http: HttpClient) { }

  getMyProjects(estado?: string): Observable<KanbanProject[]> {
    let params = new HttpParams();
    if (estado) { params = params.set('estado', estado); }
    return this.http.get<KanbanProject[]>(`${this.baseUrl}/my-projects`, { params });
  }

  getBoard(proyectoId: number, filters?: KanbanFilterState): Observable<KanbanBoard> {
    let params = new HttpParams();
    if (filters) {
      if (filters.search) { params = params.set('search', filters.search); }
      if (filters.empleadoId) { params = params.set('empleadoId', filters.empleadoId.toString()); }
      if (filters.onlyMine) { params = params.set('onlyMine', 'true'); }
      if (filters.overdue) { params = params.set('overdue', 'true'); }
      if (filters.thisWeek) { params = params.set('thisWeek', 'true'); }
      if (filters.dateFrom) { params = params.set('dateFrom', filters.dateFrom); }
      if (filters.dateTo) { params = params.set('dateTo', filters.dateTo); }
    }
    return this.http.get<KanbanBoard>(
      `${this.baseUrl}/projects/${proyectoId}/board`, { params }
    ).pipe(map(board => this.parseBoard(board)));
  }

  moveTask(proyectoId: number, hitoProyectoId: number, targetLaneId: number, sortOrder?: number): Observable<KanbanTask> {
    const body: any = { targetLaneId };
    if (sortOrder !== undefined) {
      body.sortOrder = sortOrder;
    }
    // RN-1/RN-2b (PMO): el 422 de estas reglas se maneja con mensaje específico en el
    // facade (err.error.detail) — se omite el modal genérico del interceptor global.
    return this.http.patch<KanbanTask>(
      `${this.baseUrl}/projects/${proyectoId}/tasks/${hitoProyectoId}/move`,
      body,
      { context: new HttpContext().set(SKIP_ERROR_HANDLER, true) }
    ).pipe(map(task => this.parseTask(task)));
  }

  adjustProgress(proyectoId: number, hitoProyectoId: number, porcentajeAvance: number): Observable<KanbanTask> {
    return this.http.patch<KanbanTask>(
      `${this.baseUrl}/projects/${proyectoId}/tasks/${hitoProyectoId}/progress`,
      { porcentajeAvance },
      { context: new HttpContext().set(SKIP_ERROR_HANDLER, true) }
    ).pipe(map(task => this.parseTask(task)));
  }

  blockTask(proyectoId: number, hitoProyectoId: number, blocked: boolean): Observable<KanbanTask> {
    return this.http.patch<KanbanTask>(
      `${this.baseUrl}/projects/${proyectoId}/tasks/${hitoProyectoId}/block`,
      { blocked }
    ).pipe(map(task => this.parseTask(task)));
  }

  /** Actualiza la historia de usuario de una tarea (cualquier usuario autenticado). Genera revisión. */
  updateHistoriaUsuario(hitoProyectoId: number, historiaUsuario: string): Observable<KanbanTask> {
    return this.http.patch<KanbanTask>(
      `${environment.backEndHost + environment.serverEndPoint}/hito-proyectos/${hitoProyectoId}/historia-usuario`,
      { historiaUsuario }
    ).pipe(map(task => this.parseTask(task)));
  }

  /** Historial de versiones (estilo Git) de la historia de usuario de una tarea. */
  getHistoriaUsuarioHistorial(hitoProyectoId: number): Observable<HistoriaUsuarioRevision[]> {
    return this.http.get<HistoriaUsuarioRevision[]>(
      `${environment.backEndHost + environment.serverEndPoint}/hito-proyectos/${hitoProyectoId}/historia-usuario/historial`
    );
  }

  reorderTasks(proyectoId: number, laneId: number, tasks: Array<{ taskId: number, sortOrder: number }>): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/projects/${proyectoId}/lanes/${laneId}/tasks/reorder`,
      { tasks }
    );
  }

  getLanes(proyectoId: number): Observable<KanbanLaneConfig[]> {
    return this.http.get<KanbanLaneConfig[]>(
      `${this.baseUrl}/projects/${proyectoId}/lanes`
    );
  }

  setLanes(proyectoId: number, lanes: KanbanLaneConfigInput[]): Observable<KanbanLaneConfig[]> {
    return this.http.put<KanbanLaneConfig[]>(
      `${this.baseUrl}/projects/${proyectoId}/lanes`,
      lanes
    );
  }

  resetLanes(proyectoId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/projects/${proyectoId}/lanes`
    );
  }

  /**
   * Actualiza el campo autoUpdateKanbanPercentage del proyecto.
   * Usa el endpoint estándar de proyectos (fuera del contexto kanban).
   */
  updateAutoUpdateKanbanPercentage(proyectoId: number, value: boolean): Observable<any> {
    const proyectosUrl = environment.backEndHost + environment.serverEndPoint + '/proyectos';
    return this.http.patch<any>(`${proyectosUrl}/${proyectoId}`, { id: proyectoId, autoUpdateKanbanPercentage: value });
  }

  // --- Private helpers ---

  private parseBoard(board: any): KanbanBoard {
    const lanes = (board.lanes || [])
      .map((lane: any) => ({
        ...lane,
        tasks: (lane.tasks || []).map((task: any) => this.parseTask(task))
      }))
      .sort((a: KanbanLane, b: KanbanLane) => a.position - b.position);
    return { ...board, lanes };
  }

  private parseTask(task: any): KanbanTask {
    return {
      ...task,
      descripcion: task.descripcion ?? null,
      historiaUsuario: task.historiaUsuario ?? null,
      porcentajeAvance: task.porcentajeAvance ?? 0,
      isCritical: task.isCritical ?? false,
      fechaInicio: task.fechaInicio ? this.parseLocalDate(task.fechaInicio) : null,
      fechaFin: task.fechaFin ? this.parseLocalDate(task.fechaFin) : null,
      empleadoId: task.empleadoId ?? null,
      empleadoNombre: task.empleadoNombre ?? null,
      empleadoAvatarId: task.empleadoAvatarId ?? null,
      categoria: task.categoria ?? null,
      duracionHoras: task.duracionHoras ?? null,
      costoActualHoras: task.costoActualHoras ?? null,
      hasReportedHours: task.hasReportedHours ?? false
    };
  }

  /**
   * Parse a date string in YYYY-MM-DD format to Date using LOCAL timezone
   * (not UTC, to avoid -1 day offset in negative timezones)
   */
  private parseLocalDate(dateValue: any): Date | undefined {
    return ParsingUtilities.parseLocalDate(dateValue);
  }
}
